// v3.18-1347 PAYROLL-001 — Assign Commission drawer.
//
// Renders inside the Revenue Breakdown modal on MoneyManagerPage. Payroll
// admins pick an employee; the backend auto-loads every commission rule
// on that employee's compensation plan(s) with amounts pre-computed
// against the current deal. Only "Custom Bonus" accepts a manual dollar
// amount. Save writes straight to `compensation_ledger` via
// `POST /api/money-manager/assign-commissions`.

import { useEffect, useMemo, useState } from 'react';
import { Loader2, UserPlus, DollarSign, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString('en-US', {
    style: 'currency', currency: 'USD',
  });

export default function AssignCommissionDrawer({ isOpen, onClose, dealId, onAssigned }) {
  const [employeeQuery, setEmployeeQuery]   = useState('');
  const [employees, setEmployees]           = useState([]);
  const [selectedEmployee, setSelectedEmp]  = useState(null);
  const [catalog, setCatalog]               = useState([]);
  const [selections, setSelections]         = useState({});   // rule_id → {checked, note}
  const [customRows, setCustomRows]         = useState([]);   // [{amount, label, note}]
  const [loadingEmployees, setLoadingEmp]   = useState(false);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [saving, setSaving]                 = useState(false);
  // v3.18-1353 — surface backend diagnostic when the list is genuinely empty
  const [empDiagnostic, setEmpDiagnostic]   = useState(null);
  const [empFetchError, setEmpFetchError]   = useState(null);

  // v3.18-1350 EMPLOYEE-AUTO-POPULATE — the drawer used to make the
  // operator type before showing anyone, and the endpoint returned []
  // whenever no user had compensation_plan_ids. Both are fixed now:
  // the endpoint returns every ACTIVE INTERNAL EMPLOYEE with company +
  // position, and the drawer filters the loaded list client-side.
  const filteredEmployees = useMemo(() => {
    const q = employeeQuery.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(e => {
      const haystack = [
        e.full_name, e.name, e.email, e.id, e.role,
        e.position, e.company_name,
      ].map(v => (v || '').toString().toLowerCase()).join(' | ');
      return haystack.includes(q);
    });
  }, [employees, employeeQuery]);

  // Reset on open — fetch once with no query and rely on client-side filtering.
  useEffect(() => {
    if (isOpen) {
      setEmployeeQuery('');
      setSelectedEmp(null);
      setCatalog([]);
      setSelections({});
      setCustomRows([]);
      fetchEmployees();
    }
  }, [isOpen]);

  const fetchEmployees = async () => {
    setLoadingEmp(true);
    setEmpFetchError(null);
    setEmpDiagnostic(null);
    try {
      const { data } = await api.get('/money-manager/assignable-employees', {
        params: { limit: 500 },
      });
      setEmployees(data?.items || []);
      // v3.18-1353 — if we got a 200 but empty items, auto-fetch the
      // diagnostic so the operator immediately sees WHY.
      if ((data?.items || []).length === 0) {
        try {
          const d = await api.get('/money-manager/assignable-employees/diagnose');
          setEmpDiagnostic(d?.data || null);
        } catch (_) { /* diagnostic is best-effort */ }
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e.message;
      setEmpFetchError(`HTTP ${status || 'ERR'} — ${detail}`);
      toast.error(`Failed to load employees: HTTP ${status || 'ERR'}`);
    } finally {
      setLoadingEmp(false);
    }
  };

  const fetchCatalog = async (employeeId) => {
    setLoadingCatalog(true);
    setCatalog([]);
    setSelections({});
    try {
      const params = dealId ? { deal_id: dealId } : {};
      const { data } = await api.get(`/money-manager/employee-commission-catalog/${employeeId}`, { params });
      setCatalog(data?.items || []);
    } catch (e) {
      toast.error('Failed to load commission catalog');
    } finally {
      setLoadingCatalog(false);
    }
  };

  const selectEmployee = (emp) => {
    setSelectedEmp(emp);
    fetchCatalog(emp.id);
  };

  const toggleRule = (ruleId) => {
    setSelections((prev) => ({
      ...prev,
      [ruleId]: { checked: !prev[ruleId]?.checked, note: prev[ruleId]?.note || '' },
    }));
  };

  const updateRuleNote = (ruleId, note) => {
    setSelections((prev) => ({
      ...prev,
      [ruleId]: { checked: prev[ruleId]?.checked ?? false, note },
    }));
  };

  const addCustomRow = () => {
    setCustomRows((prev) => [...prev, { amount: '', label: 'Custom Bonus', note: '' }]);
  };

  const updateCustomRow = (idx, patch) => {
    setCustomRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const removeCustomRow = (idx) => {
    setCustomRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const totalPreview = useMemo(() => {
    let total = 0;
    for (const item of catalog) {
      if (item.custom_bonus) continue;
      const sel = selections[item.rule_id];
      if (sel?.checked) total += Number(item.amount) || 0;
    }
    for (const c of customRows) total += Number(c.amount) || 0;
    return total;
  }, [catalog, selections, customRows]);

  const save = async () => {
    if (!selectedEmployee) {
      toast.error('Select an employee first');
      return;
    }
    const payloadSelections = [];
    for (const item of catalog) {
      if (item.custom_bonus) continue;
      const sel = selections[item.rule_id];
      if (sel?.checked) {
        payloadSelections.push({
          rule_id: item.rule_id,
          note:    sel.note || null,
        });
      }
    }
    for (const c of customRows) {
      const amt = Number(c.amount);
      if (!amt || amt <= 0) {
        toast.error('Custom bonus amount must be greater than 0');
        return;
      }
      payloadSelections.push({
        custom_bonus: true,
        amount:       amt,
        label:        c.label || 'Custom Bonus',
        note:         c.note || null,
      });
    }
    if (payloadSelections.length === 0) {
      toast.error('Select at least one commission to assign');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post('/money-manager/assign-commissions', {
        employee_id: selectedEmployee.id,
        deal_id:     dealId || null,
        selections:  payloadSelections,
      });
      toast.success(`Assigned ${data.assigned} commission(s) to ${selectedEmployee.full_name || selectedEmployee.email}. Status: Pending Approval.`);
      if (onAssigned) onAssigned(data);
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to assign commissions';
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const empName = (e) => e.full_name || e.name || e.email || e.id;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex justify-end"
      onClick={onClose}
      data-testid="assign-commission-drawer-backdrop"
    >
      <div
        className="bg-white w-[540px] max-w-[95vw] h-full overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="assign-commission-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b bg-slate-50">
          <div>
            <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-emerald-600" />
              Assign Commission to Employee
            </h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Writes directly to the compensation ledger with status Pending Approval.
              {dealId ? '' : ' No deal linked — only fixed amounts and custom bonuses will be available.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            data-testid="assign-commission-close-btn"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Step 1 — Employee */}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">
              Step 1 · Select Employee
            </div>
            {selectedEmployee ? (
              <div className="flex items-center justify-between rounded border border-emerald-200 bg-emerald-50 px-3 py-2" data-testid="assign-commission-selected-employee">
                <div>
                  <div className="text-sm font-semibold text-emerald-900">{empName(selectedEmployee)}</div>
                  <div className="text-[11px] text-emerald-800">
                    {selectedEmployee.company_name || <span className="italic opacity-70">No company</span>}
                    {selectedEmployee.position && <> · <b>{selectedEmployee.position}</b></>}
                  </div>
                  <div className="text-[11px] text-emerald-700 opacity-80">{selectedEmployee.email}</div>
                </div>
                <button
                  type="button"
                  onClick={() => { setSelectedEmp(null); setCatalog([]); setSelections({}); }}
                  className="text-[11px] text-emerald-700 hover:underline"
                  data-testid="assign-commission-change-employee"
                >Change</button>
              </div>
            ) : (
              <>
                <Input
                  value={employeeQuery}
                  onChange={(e) => setEmployeeQuery(e.target.value)}
                  placeholder="Filter loaded employees by name, email, role, or company…"
                  className="h-9 text-sm"
                  data-testid="assign-commission-employee-search"
                />
                <div className="mt-2 border rounded max-h-80 overflow-y-auto" data-testid="assign-commission-employee-list">
                  {loadingEmployees ? (
                    <div className="p-3 text-center text-xs text-slate-500 flex items-center justify-center gap-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading employees…
                    </div>
                  ) : employees.length === 0 ? (
                    <div className="p-4 text-center text-xs text-slate-500 italic" data-testid="assign-commission-employee-empty">
                      No active internal employees found. Add employees to an internal company to see them here.
                    </div>
                  ) : filteredEmployees.length === 0 ? (
                    <div className="p-3 text-center text-xs text-slate-400 italic" data-testid="assign-commission-employee-filter-empty">
                      No employees match &ldquo;{employeeQuery}&rdquo;. Clear the filter to see everyone.
                    </div>
                  ) : (
                    filteredEmployees.map((e) => (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => selectEmployee(e)}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-0"
                        data-testid={`assign-commission-employee-${e.id}`}
                      >
                        <div className="text-sm font-semibold text-slate-800 truncate">{empName(e)}</div>
                        <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-2">
                          {e.company_name && <span data-testid={`assign-commission-employee-company-${e.id}`}>{e.company_name}</span>}
                          {e.company_name && e.position && <span className="text-slate-300">·</span>}
                          {e.position && <span className="text-slate-600 font-medium" data-testid={`assign-commission-employee-position-${e.id}`}>{e.position}</span>}
                        </div>
                        {e.email && (
                          <div className="text-[10px] text-slate-400 mt-0.5">{e.email}</div>
                        )}
                      </button>
                    ))
                  )}
                </div>
                <div className="text-[10px] text-slate-400 mt-1 flex items-center justify-between">
                  <span data-testid="assign-commission-employee-count">
                    {loadingEmployees ? 'Loading…' :
                     `${filteredEmployees.length} of ${employees.length} employees`}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Step 2 — Catalog */}
          {selectedEmployee && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">
                Step 2 · Pick Commissions from {empName(selectedEmployee)}&apos;s Compensation Plan
              </div>
              {loadingCatalog ? (
                <div className="p-6 text-center text-xs text-slate-500 flex items-center justify-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading catalog…
                </div>
              ) : catalog.length === 0 ? (
                <div className="p-6 text-center text-xs text-slate-400 italic">
                  No commission rules configured on this employee&apos;s plan.
                </div>
              ) : (
                <div className="border rounded divide-y">
                  {catalog.filter((r) => !r.custom_bonus).map((item) => {
                    const sel = selections[item.rule_id];
                    const disabled = item.requires_deal && !dealId;
                    return (
                      <div
                        key={item.rule_id}
                        className={'p-3 ' + (disabled ? 'opacity-50' : '')}
                        data-testid={`catalog-row-${item.rule_id}`}
                      >
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!sel?.checked}
                            disabled={disabled}
                            onChange={() => toggleRule(item.rule_id)}
                            className="mt-1"
                            data-testid={`catalog-checkbox-${item.rule_id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-sm font-semibold text-slate-800 truncate">
                                {item.label}
                              </div>
                              <div className="text-sm font-bold text-emerald-700 whitespace-nowrap" data-testid={`catalog-amount-${item.rule_id}`}>
                                {fmtMoney(item.amount)}
                              </div>
                            </div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">
                              {item.event} · {item.calculation_type}{item.plan_code ? ` · ${item.plan_code}` : ''}
                            </div>
                            {disabled && (
                              <div className="text-[11px] text-amber-700 mt-1">
                                Requires a deal context (open Assign from a revenue row with a linked deal).
                              </div>
                            )}
                            {sel?.checked && (
                              <Input
                                value={sel.note || ''}
                                onChange={(e) => updateRuleNote(item.rule_id, e.target.value)}
                                placeholder="Optional note…"
                                className="h-7 text-xs mt-1.5"
                                data-testid={`catalog-note-${item.rule_id}`}
                              />
                            )}
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Custom bonuses */}
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">
                  Custom Bonuses
                </div>
                {customRows.length === 0 && (
                  <div className="text-xs text-slate-400 italic mb-2">
                    Add a custom bonus with a manual dollar amount.
                  </div>
                )}
                {customRows.map((c, idx) => (
                  <div key={idx} className="border rounded p-3 mb-2 bg-amber-50/50" data-testid={`custom-bonus-row-${idx}`}>
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-4">
                        <label className="text-[10px] uppercase tracking-wider text-slate-500">Amount</label>
                        <div className="flex items-center gap-1">
                          <DollarSign className="h-3.5 w-3.5 text-slate-400" />
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={c.amount}
                            onChange={(e) => updateCustomRow(idx, { amount: e.target.value })}
                            placeholder="0.00"
                            className="h-8 text-sm"
                            data-testid={`custom-bonus-amount-${idx}`}
                          />
                        </div>
                      </div>
                      <div className="col-span-7">
                        <label className="text-[10px] uppercase tracking-wider text-slate-500">Label</label>
                        <Input
                          value={c.label}
                          onChange={(e) => updateCustomRow(idx, { label: e.target.value })}
                          className="h-8 text-sm"
                          data-testid={`custom-bonus-label-${idx}`}
                        />
                      </div>
                      <div className="col-span-1 flex items-end">
                        <button
                          type="button"
                          onClick={() => removeCustomRow(idx)}
                          className="h-8 text-rose-600 hover:text-rose-800"
                          data-testid={`custom-bonus-remove-${idx}`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="col-span-12">
                        <label className="text-[10px] uppercase tracking-wider text-slate-500">Note</label>
                        <Input
                          value={c.note}
                          onChange={(e) => updateCustomRow(idx, { note: e.target.value })}
                          placeholder="Optional note…"
                          className="h-8 text-xs"
                          data-testid={`custom-bonus-note-${idx}`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addCustomRow}
                  className="text-xs"
                  data-testid="assign-commission-add-custom-bonus"
                >
                  + Add Custom Bonus
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t bg-slate-50 flex items-center justify-between">
          <div className="text-xs text-slate-600" data-testid="assign-commission-total-preview">
            Total to assign:&nbsp;
            <span className="font-bold text-slate-900">{fmtMoney(totalPreview)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose} data-testid="assign-commission-cancel-btn">
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={saving || !selectedEmployee}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="assign-commission-save-btn"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}
              Assign
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
